import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, AlertCircle, PlayCircle } from 'lucide-react';
import { videoComprehensionServices } from '../../api/videoComprehensionServices';
import { useAuth } from '@features/auth';
import styles from './PostVideoComprehension.module.css';

interface PostVideoComprehensionProps {
    id: string;
    question: string;
    options: string[];
    correctAnswer: string;
    onPass: () => void;
    isPassed?: boolean;
}

const PostVideoComprehension: React.FC<PostVideoComprehensionProps> = ({
    id,
    question,
    options,
    correctAnswer,
    onPass,
    isPassed: initialIsPassed = false
}) => {
    const { user } = useAuth();
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const [status, setStatus] = useState<'unanswered' | 'incorrect' | 'correct'>(
        initialIsPassed ? 'correct' : 'unanswered'
    );
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (initialIsPassed) {
            setStatus('correct');
        }
    }, [initialIsPassed]);

    const handleOptionSelect = async (option: string) => {
        if (status === 'correct' || isSubmitting) return;

        setSelectedOption(option);
        
        if (option === correctAnswer) {
            setIsSubmitting(true);
            try {
                if (user) {
                    await videoComprehensionServices.recordPass(user.uid, id);
                }
                setStatus('correct');
                onPass();
            } catch (error) {
                console.error('Failed to record PVC pass:', error);
                // Even if recording fails, we might want to let them through if it's correct?
                // But the requirement says record it.
                setStatus('correct');
                onPass();
            } finally {
                setIsSubmitting(false);
            }
        } else {
            setStatus('incorrect');
        }
    };

    const handleRetry = () => {
        setSelectedOption(null);
        setStatus('unanswered');
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`${styles.container} ${status === 'correct' ? styles.passed : ''}`}
        >
            <div className={styles.header}>
                <div className={styles.iconWrapper}>
                    {status === 'correct' ? (
                        <CheckCircle className={styles.successIcon} />
                    ) : (
                        <PlayCircle className={styles.pvcIcon} />
                    )}
                </div>
                <div className={styles.headerText}>
                    <span className={styles.label}>Video Quick Check</span>
                    <h4 className={styles.question}>{question}</h4>
                </div>
            </div>

            <div className={styles.optionsGrid}>
                {options.map((option, index) => (
                    <button
                        key={index}
                        onClick={() => handleOptionSelect(option)}
                        disabled={status === 'correct' || isSubmitting}
                        className={`
                            ${styles.optionButton}
                            ${selectedOption === option && status === 'incorrect' ? styles.optionWrong : ''}
                            ${status === 'correct' && option === correctAnswer ? styles.optionCorrect : ''}
                        `}
                    >
                        <span className={styles.optionLetter}>
                            {String.fromCharCode(65 + index)}
                        </span>
                        <span className={styles.optionText}>{option}</span>
                    </button>
                ))}
            </div>

            <AnimatePresence mode="wait">
                {status === 'incorrect' && (
                    <motion.div
                        key="incorrect"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className={styles.feedbackWrapper}
                    >
                        <div className={styles.failureFeedback}>
                            <AlertCircle size={18} />
                            <span>Wrong, please try again.</span>
                            <button onClick={handleRetry} className={styles.retryLink}>
                                Retry
                            </button>
                        </div>
                    </motion.div>
                )}

                {status === 'correct' && (
                    <motion.div
                        key="correct"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        className={styles.feedbackWrapper}
                    >
                        <div className={styles.successFeedback}>
                            <CheckCircle size={18} />
                            <span>Correct! You may now proceed.</span>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

export default PostVideoComprehension;
